<template>
  <div class="lineup text-xs px-1 pb-2">
    <div class="flex justify-between items-center mb-2 text-[10px] uppercase tracking-wide font-semibold text-gray-500">
      <span class="flex items-center gap-1.5">
        <span class="w-2.5 h-2.5 rounded-full bg-[#BDBDFB]" aria-hidden="true"></span>
        {{ away.name }}<template v-if="awayFormation"> · {{ awayFormation }}</template>
      </span>
      <span
        class="px-2 py-0.5 rounded-full normal-case tracking-normal"
        :class="confirmed ? 'bg-[#22c55e] text-white' : 'bg-gray-800/10 text-gray-600'"
        >{{ confirmed ? "Confirmed" : "Predicted" }}</span
      >
    </div>

    <div v-if="awayLines.length || homeLines.length" class="pitch relative rounded-lg overflow-hidden">
      <div class="pitch-lines" aria-hidden="true">
        <span class="halfway"></span><span class="circle"></span>
        <span class="box top"></span><span class="box bottom"></span>
      </div>
      <!-- away: keeper at the top -->
      <div class="half">
        <div v-for="(line, i) in awayLines" :key="`a${i}`" class="line">
          <div v-for="p in line" :key="p.key" class="player">
            <span class="dot away">{{ p.number || "" }}</span>
            <span class="name" :title="p.player">{{ surname(p.player) }}</span>
          </div>
        </div>
      </div>
      <!-- home: keeper at the bottom -->
      <div class="half">
        <div v-for="(line, i) in homeLinesReversed" :key="`h${i}`" class="line">
          <div v-for="p in line" :key="p.key" class="player">
            <span class="dot home">{{ p.number || "" }}</span>
            <span class="name" :title="p.player">{{ surname(p.player) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-between items-center mt-2 text-[10px] uppercase tracking-wide font-semibold text-gray-500">
      <span class="flex items-center gap-1.5">
        <span class="w-2.5 h-2.5 rounded-full bg-[#E59C98]" aria-hidden="true"></span>
        {{ home.name }}<template v-if="homeFormation"> · {{ homeFormation }}</template>
      </span>
    </div>

    <p v-if="!confirmed" class="text-center text-[10px] text-gray-500 mt-2">
      Official lineups come out about an hour before kick-off.
    </p>
  </div>
</template>

<script>
const POSITION_ORDER = { 24: 0, 25: 1, 26: 2, 27: 3 }; // keeper, defence, midfield, attack

function lines(lineup, formation) {
  const players = (lineup || []).map((p, i) => ({ ...p, key: `${p.player_id || p.player}-${i}` }));
  if (!players.length) return [];
  const slotted = players.filter((p) => p.formation_position).sort((a, b) => a.formation_position - b.formation_position);
  const shape = String(formation || "")
    .split("-")
    .map(Number)
    .filter((n) => n > 0);
  // formation slots: 1 is the keeper, then each line of the formation in order
  if (shape.length && slotted.length === players.length && shape.reduce((a, b) => a + b, 0) + 1 === players.length) {
    const out = [slotted.slice(0, 1)];
    let i = 1;
    for (const n of shape) {
      out.push(slotted.slice(i, i + n));
      i += n;
    }
    return out;
  }
  // no formation: group by position (keeper, defence, midfield, attack)
  if (players.every((p) => p.position_id in POSITION_ORDER)) {
    const groups = [[], [], [], []];
    players.forEach((p) => groups[POSITION_ORDER[p.position_id]].push(p));
    return groups.filter((g) => g.length);
  }
  // nothing to place them by: rows of four
  const out = [];
  for (let i = 0; i < players.length; i += 4) out.push(players.slice(i, i + 4));
  return out;
}

export default {
  name: "LineupPitch",
  props: {
    home: { type: Object, required: true }, // {name, predicted_lineup, formation}
    away: { type: Object, required: true },
    confirmed: { type: Boolean, default: false },
  },
  computed: {
    homeFormation() {
      return this.home.formation || "";
    },
    awayFormation() {
      return this.away.formation || "";
    },
    homeLines() {
      return lines(this.home.predicted_lineup, this.home.formation);
    },
    homeLinesReversed() {
      return [...this.homeLines].reverse();
    },
    awayLines() {
      return lines(this.away.predicted_lineup, this.away.formation);
    },
  },
  methods: {
    surname(name) {
      const parts = (name || "").split(" ");
      return parts.length > 1 ? parts.slice(1).join(" ") : name;
    },
  },
};
</script>

<style scoped>
.pitch {
  background: repeating-linear-gradient(180deg, #e8f3ea 0 40px, #dfeee2 40px 80px);
  border: 1px solid #cfe3d3;
  display: grid;
  grid-template-rows: 1fr 1fr;
  min-height: 460px;
}
.pitch-lines {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.pitch-lines span {
  position: absolute;
  border: 1.5px solid rgba(255, 255, 255, 0.9);
}
.pitch-lines .halfway {
  left: 0;
  right: 0;
  top: 50%;
  border-width: 1.5px 0 0;
}
.pitch-lines .circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  left: calc(50% - 35px);
  top: calc(50% - 35px);
}
.pitch-lines .box {
  width: 44%;
  height: 46px;
  left: 28%;
}
.pitch-lines .box.top {
  top: -1.5px;
}
.pitch-lines .box.bottom {
  bottom: -1.5px;
}
.half {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 8px 4px;
}
.line {
  display: flex;
  justify-content: space-around;
  gap: 2px;
}
.player {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  max-width: 64px;
  min-width: 0;
}
.dot {
  width: 26px;
  height: 26px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #1f2937;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.dot.home {
  background: #e59c98;
}
.dot.away {
  background: #bdbdfb;
}
.name {
  margin-top: 2px;
  font-size: 10px;
  line-height: 1.1;
  color: #1f2937;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  background: rgba(255, 255, 255, 0.75);
  padding: 0 3px;
  border-radius: 3px;
}
</style>
